import React, { useRef, useMemo, useEffect, useCallback, useLayoutEffect, } from 'react';
import Animated, { runOnJS, withTiming, interpolate, useSharedValue, interpolateColor, useAnimatedStyle, useAnimatedScrollHandler, } from 'react-native-reanimated';
import { View } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import StatusBar from 'design-system/StatusBar';
import ListLoader from 'design-system/ListLoader';
import FakeSearchBar from 'design-system/FakeSearchBar';
import ListEmptyView from 'design-system/ListEmptyView';
import { AppView, NavigationHeader } from 'design-system';
import SearchBar from 'design-system/SearchBar';
import { rem, SCREEN_BOTTOM_PADDING } from 'design-system/values';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import Amplitude from 'utils/amplitude';
import { useSearchField } from 'hooks/useSearchField';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { isWeb } from '../../constants';
import { useRewards } from './hooks/useRewards';
import { RewardListItem } from './components/RewardListItem';
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const keyExtractor = (item) => item.id.toString();
const SearchRewards = ({ route, navigation, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const searchBarY = route.params?.searchBarY;
    const { top } = useSafeAreaInsets();
    const offset = (searchBarY || 0) - rem(8);
    const scrollY = useSharedValue(0);
    const searchBarRef = useRef(null);
    const { searchTerm, changeSearchTerm } = useSearchField();
    const { hasRewards, fetchRewards, isRewardsFetching, filteredBySearchRewards, openReward, } = useRewards(searchTerm);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: false,
        });
    }, []);
    useEffect(() => {
        if (!hasRewards) {
            fetchRewards();
        }
    }, [fetchRewards, hasRewards]);
    const progressSharedValue = useSharedValue(isWeb ? 1 : 0);
    const focusSearch = useCallback(() => {
        searchBarRef.current?.focus();
    }, []);
    useEffect(() => {
        if (!isWeb) {
            progressSharedValue.value = withTiming(1, undefined, () => {
                runOnJS(focusSearch)();
            });
        }
        else {
            focusSearch();
        }
    }, [focusSearch, progressSharedValue]);
    const textInputStyle = useAnimatedStyle(() => ({
        backgroundColor: interpolateColor(scrollY.value, [0, 40], [
            colors.cards.onDark,
            colors.background.dark,
        ]),
    }), [colors, scrollY]);
    const fakeSearchBarStyles = useAnimatedStyle(() => ({
        transform: [
            {
                translateY: interpolate(progressSharedValue.value, [0, 1], [offset, top], 'clamp'),
            },
        ],
        opacity: progressSharedValue.value === 1 ? 0 : 1,
    }), [offset, progressSharedValue, top]);
    const handleCancel = useCallback(() => {
        if (!isWeb) {
            progressSharedValue.value = withTiming(0, {
                duration: 200,
            }, () => {
                runOnJS(navigation.pop)();
            });
        }
        else {
            navigation.pop();
        }
    }, [progressSharedValue]);
    const onOpenReward = useCallback((reward) => {
        Amplitude.logEvent('Open Reward', { source: 'rewards', reward });
        openReward(reward);
    }, [openReward]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle, styles.contentContainerStyle);
    const renderItem = useCallback(({ item, index }) => (<RewardListItem key={item.id} index={index} reward={item} onPress={onOpenReward} itemsLength={filteredBySearchRewards.length}/>), [filteredBySearchRewards.length, onOpenReward]);
    const onScroll = useAnimatedScrollHandler((e) => {
        scrollY.value = e.contentOffset.y;
    });
    const listAnimatedStyle = useAnimatedStyle(() => ({
        opacity: interpolate(progressSharedValue.value, [0.5, 1], [0, 1], 'clamp'),
    }));
    const searchBarAnimatedStyle = useAnimatedStyle(() => ({
        opacity: progressSharedValue.value === 1 ? 1 : 0,
    }));
    const EmptyComponent = useMemo(() => {
        if (isRewardsFetching) {
            return <ListLoader smallMargin/>;
        }
        return (<ListEmptyView alt onCard happy title="No results found" subTitle="Try searching with a different word or check our featured cashback offers"/>);
    }, [isRewardsFetching]);
    const Content = useMemo(() => {
        const baseProps = {
            onScroll,
            renderItem,
            keyExtractor,
            contentContainerStyle,
            scrollEventThrottle: 16,
            data: filteredBySearchRewards,
            ListEmptyComponent: EmptyComponent,
            keyboardDismissMode: 'on-drag',
            keyboardShouldPersistTaps: 'handled',
        };
        if (isWeb) {
            return <Animated.FlatList {...baseProps}/>;
        }
        return <AnimatedFlashList {...baseProps}/>;
    }, [
        EmptyComponent,
        contentContainerStyle,
        filteredBySearchRewards,
        onScroll,
        renderItem,
    ]);
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <View style={styles.flex}>
        <Animated.View style={searchBarAnimatedStyle}>
          <NavigationHeader scrollY={scrollY} noPaddingTop>
            <View style={paddingHorizontalStyle}>
              <SearchBar alwaysShowCancel ref={searchBarRef} onCancel={handleCancel} placeholder="Search cashback" containerStyle={styles.searchBar} textInputBgStyles={textInputStyle} onChangeSearchTerm={changeSearchTerm}/>
            </View>
          </NavigationHeader>
        </Animated.View>
        <Animated.View style={[styles.flex, listAnimatedStyle]}>
          {Content}
        </Animated.View>
      </View>
      {!isWeb && (<Animated.View style={[
                styles.fakeSearchBar,
                fakeSearchBarStyles,
                paddingHorizontalStyle,
            ]} pointerEvents="none">
          <FakeSearchBar placeholder="Search cashback"/>
        </Animated.View>)}
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    flex: {
        flex: 1,
    },
    content: {
        paddingTop: rem(16),
    },
    indicator: {
        paddingBottom: rem(200),
    },
    searchBar: {
        marginTop: rem(isWeb ? 24 : 8),
        paddingTop: rem(8),
    },
    contentContainerStyle: {
        paddingTop: rem(16),
    },
    fakeSearchBar: {
        left: 0,
        right: 0,
        marginTop: rem(8),
        position: 'absolute',
    },
}));
export default SearchRewards;
